import * as React from 'react';
import {cn} from '../lib/cn';
import {InputOTPContext} from './InputOTPContext';
import {useInputOTP} from './useInputOTP';
import {useInputOTPSlot} from './useInputOTPSlot';

export type InputOTPProps = {
  maxLength: number;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  children: React.ReactNode;
  className?: string;
  autoFocus?: boolean;
};

function InputOTP({
  maxLength,
  value = '',
  onChange,
  disabled = false,
  error = false,
  children,
  className,
  autoFocus = false,
}: InputOTPProps) {
  const {containerRef, contextValue, handleContainerFocusIn, handleContainerFocusOut} =
    useInputOTP({maxLength, value, onChange, disabled, autoFocus, error});

  return (
    <InputOTPContext.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={cn('flex items-center gap-2', disabled && 'opacity-50', className)}
        role="group"
        aria-label="One-time password input"
        onFocus={handleContainerFocusIn}
        onBlur={handleContainerFocusOut}
      >
        {children}
      </div>
    </InputOTPContext.Provider>
  );
}

type InputOTPGroupProps = React.ComponentPropsWithoutRef<'div'>;

const InputOTPGroup = React.forwardRef<HTMLDivElement, InputOTPGroupProps>(
  ({className, ...props}, ref) => (
    <div ref={ref} className={cn('flex items-center', className)} {...props} />
  ),
);
InputOTPGroup.displayName = 'InputOTPGroup';

type InputOTPSlotProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> & {
  index: number;
};

const InputOTPSlot = React.forwardRef<HTMLDivElement, InputOTPSlotProps>(
  ({index, className, ...props}, ref) => {
    const {
      char,
      isActive,
      disabled,
      error,
      maxLength,
      setInputRef,
      handleKeyDown,
      handleInputChange,
      handlePasteEvent,
      focusSlot,
    } = useInputOTPSlot(index);

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex h-12 w-12 items-center justify-center border-y border-r border-[var(--chekin-color-gray-3)] text-base font-medium transition-all first:rounded-l-md first:border-l last:rounded-r-md',
          isActive && 'z-10 shadow-[var(--chekin-shadow-focus)]',
          error && 'border-[var(--error-message-color)]',
          className,
        )}
        onClick={focusSlot}
        {...props}
      >
        <input
          ref={setInputRef}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d*"
          maxLength={1}
          value={char}
          disabled={disabled}
          aria-label={`Digit ${index + 1} of ${maxLength}`}
          className="absolute inset-0 h-full w-full select-none bg-transparent text-center text-base font-medium outline-none disabled:cursor-not-allowed"
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          onPaste={handlePasteEvent}
        />
      </div>
    );
  },
);
InputOTPSlot.displayName = 'InputOTPSlot';

type InputOTPSeparatorProps = React.ComponentPropsWithoutRef<'div'>;

const InputOTPSeparator = React.forwardRef<HTMLDivElement, InputOTPSeparatorProps>(
  (props, ref) => <div ref={ref} role="separator" {...props} />,
);
InputOTPSeparator.displayName = 'InputOTPSeparator';

export {InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator};
